import { z } from "zod";
import {
  CLAIM_APPROVAL_STATUSES,
  CLAIM_PAYMENT_STATUSES,
} from "@/features/expenses/schemas/expense-claim.schema";

/// URL search params for `/$slug/expenses`. One `scope` value drives the whole
/// page (frontend-ia scope law, design.md 6.4):
///
///   mine      , my own claims (everyone, EXPENSE_CLAIM_VIEW_OWN)
///   team      , claims I am a candidate on or decided (the approver lens)
///   workspace , every claim (admins, EXPENSE_CLAIM_VIEW_WORKSPACE)
///   setup     , the expense category catalog (EXPENSE_CATEGORY_MANAGE)
///
/// `setup` rides the same param rather than a second `tab` param because it
/// is a peer tab in the same strip (ui-wireframes section 5), not a panel
/// inside a lens. It is a different perspective on the module, which is the
/// scope-tab test; it just happens to show configuration rather than claims.
///
/// Tab visibility is enforced twice: the strip hides what the caller cannot
/// use, and the route refuses a forbidden deep link by redirecting to `mine`
/// (HRMS-EXP-FR-24, Q11). `AdminOnlyCard` never applies here.
export const EXPENSE_SCOPES = ["mine", "team", "workspace", "setup"] as const;
export type ExpenseScope = (typeof EXPENSE_SCOPES)[number];

/// The two claim filters, mirroring the shore's `ListExpenseClaimsQueryDto`
/// whitelist (HRMS-EXP-004). They live in the URL rather than in component
/// state (S3) because the summary tiles LINK to them: "3 drafts" is a link to
/// this page filtered, and a filter that cannot be addressed is a tile that
/// cannot be clicked.
///
/// Both are `.catch(undefined)` rather than `.catch("pending")`: a bad value
/// in a pasted URL should show the unfiltered list, not silently answer a
/// different question than the one the link asked.
export const expensesRouteSearchSchema = z
  .object({
    scope: z.enum(EXPENSE_SCOPES).catch("mine").default("mine"),
    approvalStatus: z.enum(CLAIM_APPROVAL_STATUSES).optional().catch(undefined),
    paymentStatus: z.enum(CLAIM_PAYMENT_STATUSES).optional().catch(undefined),
  })
  .strict();
export type ExpensesRouteSearch = z.infer<typeof expensesRouteSearchSchema>;
